/**
 * Cómo se dicen en pantalla los datos del motor de rastreo.
 *
 * Cada estado lleva su palabra: nada se comunica solo con color. `null` se dice
 * «sin dato», nunca «0».
 */

import type { RastreoPosition, RastreoVehicle } from "@core/contracts/rastreo.ts";
import type { PositionFieldKey, PositionRecord, RowState } from "@core/rastreo/registro.ts";

const BOGOTA = "America/Bogota";

const timeFormat = new Intl.DateTimeFormat("es-CO", {
  timeZone: BOGOTA,
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

/** Hora de Bogotá, legible. */
export function bogotaTime(iso: string | null): string {
  if (!iso) return "sin dato";
  const at = Date.parse(iso);
  return Number.isFinite(at) ? timeFormat.format(new Date(at)) : "sin dato";
}

/** «hace 3 min», «hace 2 h», «hace 4 días». */
export function ago(iso: string | null, now: number): string {
  if (!iso) return "sin dato";
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return "sin dato";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} días`;
}

export const CONNECTION_LABEL: Record<RastreoVehicle["connectionStatus"], string> = {
  online: "En línea",
  offline: "Sin conexión",
  unknown: "Nunca se conectó",
};

export const FRESHNESS_LABEL: Record<RastreoVehicle["telemetryFreshness"], string> = {
  fresh: "Posición reciente",
  stale: "Posición vieja",
  unknown: "Sin posición",
};

export const MODEL_STATUS_LABEL: Record<RastreoVehicle["modelStatus"], string> = {
  en_prueba: "Modelo en prueba",
  soportado: "Modelo soportado",
  no_soportado: "Modelo no soportado",
};

export const IGNITION_LABEL: Record<RastreoPosition["ignition"], string> = {
  on: "Encendida",
  off: "Apagada",
  unknown: "Sin dato",
};

export const CLOCK_LABEL: Record<RastreoPosition["clockQuality"], string> = {
  ok: "Hora del equipo verificada",
  unverified: "Hora del equipo sin verificar (modelo en prueba)",
  drift: "Reloj del equipo desfasado más de 5 min",
  unknown: "El equipo no mandó hora",
};

export function fixLabel(fixValid: boolean | null): string {
  if (fixValid === true) return "GPS con señal fija";
  if (fixValid === false) return "GPS sin señal fija: puede ser la última posición conocida";
  return "Sin dato de señal GPS";
}

const number = (value: number, decimals = 0) =>
  value.toLocaleString("es-CO", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export function measure(value: number | null, unit: string, decimals = 0): string {
  return value === null ? "sin dato" : `${number(value, decimals)} ${unit}`;
}

/** Atributos conocidos del motor, con nombre y unidad. Lo desconocido se muestra tal cual. */
const ATTRIBUTES: Record<string, (value: number | boolean | string | null) => [string, string]> = {
  batteryPct: (v) => ["Batería del equipo", typeof v === "number" ? `${number(v)} %` : String(v ?? "sin dato")],
  externalPower: (v) => ["Alimentación externa", v === true ? "Conectada" : v === false ? "Desconectada" : "sin dato"],
  externalVoltageV: (v) => ["Voltaje externo", typeof v === "number" ? `${number(v, 1)} V` : "sin dato"],
  batteryVoltageV: (v) => ["Voltaje de la batería", typeof v === "number" ? `${number(v, 2)} V` : "sin dato"],
  // La escala depende del protocolo (en GT06 va de 0 a 4): se muestra el nivel tal cual.
  gsmLevel: (v) => ["Señal GSM", typeof v === "number" ? `nivel ${number(v)}` : "sin dato"],
  odometerM: (v) => ["Odómetro del equipo", typeof v === "number" ? `${number(v / 1000, 1)} km` : "sin dato"],
  motion: (v) => ["En movimiento", v === true ? "Sí" : v === false ? "No" : "sin dato"],
  alarm: (v) => ["Alarma", v === null ? "sin dato" : typeof v === "string" ? alertLabel(v).name : String(v)],
  // Códigos de la antena: son identificadores, no cantidades. Sin separador de miles, para poder copiarlos.
  mcc: (v) => ["MCC (país de la antena)", typeof v === "number" ? String(v) : "sin dato"],
  mnc: (v) => ["MNC (operador de la antena)", typeof v === "number" ? String(v) : "sin dato"],
  lac: (v) => ["LAC (área de la antena)", typeof v === "number" ? String(v) : "sin dato"],
  cellId: (v) => ["CID (antena)", typeof v === "number" ? String(v) : "sin dato"],
  cutActive: (v) => ["Corte de motor", v === true ? "Activo" : v === false ? "Inactivo" : "sin dato"],
  resent: (v) => ["Reenviada desde la memoria", v === true ? "Sí" : v === false ? "No" : "sin dato"],
};

export function describeAttributes(attributes: RastreoPosition["attributes"]): Array<[string, string]> {
  return Object.entries(attributes).map(([key, value]) => {
    const describe = ATTRIBUTES[key];
    return describe ? describe(value) : [key, value === null ? "sin dato" : String(value)];
  });
}

/** Nombre que se le muestra a una persona: nunca el IMEI, que no llega al navegador. */
export function vehicleLabel(vehicle: Pick<RastreoVehicle, "name" | "model" | "deviceId">): string {
  return vehicle.name ?? vehicle.model ?? `Equipo ${vehicle.deviceId.slice(0, 8)}`;
}

/* --------------------------------------------------------------- *
 * Panel estilo Traccar (especificación `specs/rastreo-registro/`)
 * --------------------------------------------------------------- */

export const ROW_STATE_LABEL: Record<RowState, string> = {
  online: "En línea",
  offline: "Sin conexión",
  never: "Nunca se conectó",
  unlinked: "Sin vincular al motor",
  no_motor_data: "Sin datos del motor",
};

/** Tono del texto de estado. El texto siempre va: el color solo lo refuerza (RR-02). */
export const ROW_STATE_CLASS: Record<RowState, string> = {
  online: "text-emerald-700",
  offline: "text-amber-700",
  never: "text-gd-muted",
  unlinked: "text-gd-blue",
  no_motor_data: "text-gd-muted",
};

/** Batería del equipo en %, si la mandó en su última posición. */
export function batteryOf(vehicle: RastreoVehicle | null): number | null {
  const value = vehicle?.lastPosition?.attributes["batteryPct"];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function ignitionOf(vehicle: RastreoVehicle | null): RastreoPosition["ignition"] {
  return vehicle?.lastPosition?.ignition ?? "unknown";
}

export function batteryText(level: number | null): string {
  return level === null ? "Batería: sin dato" : `Batería ${number(level)} %`;
}

export function ignitionText(ignition: RastreoPosition["ignition"]): string {
  return ignition === "on" ? "Ignición encendida" : ignition === "off" ? "Ignición apagada" : "Ignición: sin dato";
}

const yesNo = (value: boolean | null) => (value === null ? "sin dato" : value ? "Sí" : "No");

/** Un campo del informe de posiciones como texto. `null` es «sin dato», nunca 0 (Art. 3). */
export function formatPositionField(key: PositionFieldKey, record: PositionRecord): string {
  const value = record[key];
  if (value === null) return "sin dato";
  switch (key) {
    case "serverTime":
      return bogotaTime(String(value));
    case "latitude":
    case "longitude":
      return Number(value).toFixed(6);
    case "altitude":
      return measure(Number(value), "m");
    case "speed":
      return measure(Number(value), "km/h", 1);
    case "course":
      return measure(Number(value), "°");
    case "batteryLevel":
      return measure(Number(value), "%");
    case "totalDistance":
      return `${number(Number(value) / 1000, 1)} km`;
    case "event":
    case "geofence":
      return alertLabel(String(value)).name;
    case "ignition":
      return value ? "Encendida" : "Apagada";
    case "mcc":
    case "mnc":
    case "lac":
    case "cid":
      // Identificadores de la antena, no cantidades: sin separador de miles.
      return String(value);
    default:
      return typeof value === "boolean" ? yesNo(value) : typeof value === "number" ? number(value) : String(value);
  }
}

/**
 * Las alarmas que manda un GPS tipo GT06, con su nombre y lo que significan. Los códigos que el
 * catálogo no conoce llegan como `codigo_0xNN` y se muestran como alarma sin catalogar: no se adivina.
 */
export const ALERT_CATALOG: Record<string, { name: string; meaning: string; telemetry?: boolean }> = {
  sos: {
    name: "Botón SOS",
    meaning: "El GPS avisó que presionaron su botón de pánico. Es la alerta más urgente: conviene llamar al conductor.",
  },
  corte_energia: {
    name: "Desconexión de energía",
    meaning: "El GPS avisó que dejó de recibir la corriente externa del vehículo.",
    telemetry: true,
  },
  vibracion: {
    name: "Vibración",
    meaning: "El GPS avisó que detectó vibración o golpes.",
  },
  geocerca_entrada: {
    name: "Entrada a geocerca",
    meaning: "El GPS avisó que el vehículo entró a una zona configurada en el propio GPS.",
  },
  geocerca_salida: {
    name: "Salida de geocerca",
    meaning: "El GPS avisó que el vehículo salió de una zona configurada en el propio GPS.",
  },
  exceso_velocidad: {
    name: "Exceso de velocidad",
    meaning: "El GPS avisó que el vehículo pasó el límite de velocidad configurado en él.",
  },
  desplazamiento: {
    name: "Desplazamiento",
    meaning: "El GPS avisó que el vehículo se desplazó fuera de la posición que tenía configurada.",
  },
  bateria_baja: {
    name: "Batería baja",
    meaning: "El GPS avisó batería baja, sin decir cuál: no distingue su batería interna de la del vehículo.",
  },
  desmonte: {
    name: "Desmonte",
    meaning: "El GPS avisó que lo sacaron o lo manipularon en su sitio. El detalle depende del modelo.",
  },
};

/**
 * Orientación para quien atiende la alerta. NO es lo que el GPS dijo: son cosas que suelen estar
 * detrás de cada aviso y hay que confirmarlas con el conductor o el vehículo.
 */
export const ALERT_GUIDANCE: Record<string, string> = {
  sos: "Llama al conductor.",
  vibracion: "Si el vehículo estaba apagado, puede ser que alguien lo golpee o lo toque.",
  desplazamiento: "Si el vehículo debía estar quieto, puede ser un remolque o un robo: confirma con el conductor.",
  bateria_baja: "Si además se pierde la energía del vehículo, el GPS se queda sin reportar.",
  desmonte: "Puede ser un intento de retirar el GPS del vehículo.",
};

/** ¿Es un código del catálogo? `hasOwn`: «constructor» o «toString» no son alertas conocidas. */
export const isCatalogCode = (code: string): boolean => Object.hasOwn(ALERT_CATALOG, code);

/** La orientación de una alerta del catálogo, si tiene. */
export const alertGuidance = (code: string): string | null => (Object.hasOwn(ALERT_GUIDANCE, code) ? ALERT_GUIDANCE[code]! : null);

export function alertLabel(code: string): { name: string; meaning: string; telemetry: boolean } {
  const known = isCatalogCode(code) ? ALERT_CATALOG[code] : undefined;
  if (known) return { name: known.name, meaning: known.meaning, telemetry: known.telemetry === true };
  return {
    name: `Alarma sin catalogar (${code})`,
    meaning: "El GPS mandó un código de alarma que el motor todavía no sabe nombrar. Avisa a tecnología con la placa para agregarlo.",
    telemetry: false,
  };
}
